import type { TransportErrorResponse } from '@mangovoice/api-client'

import { uuidFilename } from '../utils/uuidFilename'
import { MOH_ENDPOINTS } from './endpoints'
import type {
  ApiError,
  AudioTrack,
  AudioTrackSummary,
  CreateAudioTrackPayload,
  CreatePlaylistPayload,
  MusicOnHoldApi,
  MusicOnHoldApiClient,
  Playlist,
  PlaylistListItem,
  UpdateAudioTrackPayload,
  UpdatePlaylistPayload,
} from './musicOnHoldApi.types'

function throwOnError(
  response: TransportErrorResponse,
  fallback: string,
): never {
  throw new Error(
    (response.data as ApiError | undefined)?.message ??
      `${fallback} (${response.status ?? 'unknown'})`,
  )
}

function buildAudioTrackFormData(
  payload: CreateAudioTrackPayload | UpdateAudioTrackPayload,
): FormData {
  const formData = new FormData()
  if ('pbx_id' in payload) {
    formData.append('pbx_id', String(payload.pbx_id))
  }
  if (payload.name !== undefined) {
    formData.append('name', payload.name)
  }
  if (payload.file) {
    formData.append('file', uuidFilename(payload.file))
  }
  return formData
}

export const createMusicOnHoldApi = (
  client: MusicOnHoldApiClient,
): MusicOnHoldApi => ({
  // ---------------------------------------------------------------------------
  // Playlists
  // ---------------------------------------------------------------------------

  async getPlaylists(pbxId: number): Promise<PlaylistListItem[]> {
    const response = await client.get<PlaylistListItem[]>(
      MOH_ENDPOINTS.PLAYLISTS_LIST(pbxId),
    )
    if (!response.ok) throwOnError(response, 'Failed to fetch playlists')
    return response.data as PlaylistListItem[]
  },

  async getPlaylist(playlistId: number): Promise<Playlist> {
    const response = await client.get<Playlist>(
      MOH_ENDPOINTS.PLAYLIST_DETAIL(playlistId),
    )
    if (!response.ok) throwOnError(response, 'Failed to fetch playlist')
    return response.data as Playlist
  },

  async createPlaylist(payload: CreatePlaylistPayload): Promise<Playlist> {
    const response = await client.post<Playlist>(
      MOH_ENDPOINTS.PLAYLISTS_LIST(payload.pbx_id),
      payload,
    )
    if (!response.ok) throwOnError(response, 'Failed to create playlist')
    return response.data as Playlist
  },

  async updatePlaylist(
    id: number,
    payload: UpdatePlaylistPayload,
  ): Promise<Playlist> {
    const response = await client.patch<Playlist>(
      MOH_ENDPOINTS.PLAYLIST_DETAIL(id),
      payload,
    )
    if (!response.ok) throwOnError(response, 'Failed to update playlist')
    return response.data as Playlist
  },

  async deletePlaylist(id: number): Promise<void> {
    const response = await client.delete<void>(
      MOH_ENDPOINTS.PLAYLIST_DETAIL(id),
    )
    if (!response.ok) throwOnError(response, 'Failed to delete playlist')
  },

  async setDefaultPlaylist(pbxId: number, playlistId: number): Promise<void> {
    const response = await client.post<void>(
      MOH_ENDPOINTS.PLAYLIST_DEFAULT(pbxId),
      { playlist_id: playlistId },
    )
    if (!response.ok) throwOnError(response, 'Failed to set default playlist')
  },

  // ---------------------------------------------------------------------------
  // Audio Tracks
  // ---------------------------------------------------------------------------

  async getAudioTracks(pbxId: number): Promise<AudioTrackSummary[]> {
    const response = await client.get<AudioTrackSummary[]>(
      MOH_ENDPOINTS.AUDIO_TRACKS_LIST(pbxId),
    )
    if (!response.ok) throwOnError(response, 'Failed to fetch audio tracks')
    return response.data as AudioTrackSummary[]
  },

  async getAudioTrack(pbxId: number, uuid: string): Promise<AudioTrack> {
    const response = await client.get<AudioTrack>(
      MOH_ENDPOINTS.AUDIO_TRACK_DETAIL(pbxId, uuid),
    )
    if (!response.ok) throwOnError(response, 'Failed to fetch audio track')
    return response.data as AudioTrack
  },

  async createAudioTrack(
    payload: CreateAudioTrackPayload,
  ): Promise<AudioTrack> {
    const response = await client.post<AudioTrack>(
      MOH_ENDPOINTS.AUDIO_TRACKS_LIST(payload.pbx_id),
      buildAudioTrackFormData(payload),
    )
    if (!response.ok) throwOnError(response, 'Failed to create audio track')
    return response.data as AudioTrack
  },

  async updateAudioTrack(
    pbxId: number,
    uuid: string,
    payload: UpdateAudioTrackPayload,
  ): Promise<AudioTrack> {
    if (payload.name === undefined && !payload.file) {
      const response = await client.get<AudioTrack>(
        MOH_ENDPOINTS.AUDIO_TRACK_DETAIL(pbxId, uuid),
      )
      if (!response.ok) throwOnError(response, 'Failed to fetch audio track')
      return response.data as AudioTrack
    }
    const response = await client.patch<AudioTrack>(
      MOH_ENDPOINTS.AUDIO_TRACK_DETAIL(pbxId, uuid),
      buildAudioTrackFormData(payload),
    )
    if (!response.ok) throwOnError(response, 'Failed to update audio track')
    return response.data as AudioTrack
  },

  async deleteAudioTrack(pbxId: number, uuid: string): Promise<void> {
    const response = await client.delete<void>(
      MOH_ENDPOINTS.AUDIO_TRACK_DETAIL(pbxId, uuid),
    )
    if (!response.ok) throwOnError(response, 'Failed to delete audio track')
  },

  // ---------------------------------------------------------------------------
  // Playlist-AudioTrack associations
  // ---------------------------------------------------------------------------

  async addTrackToPlaylist(
    playlistId: number,
    audioTrackId: number,
  ): Promise<void> {
    const response = await client.post<void>(
      MOH_ENDPOINTS.PLAYLIST_ADD_TRACK(playlistId),
      { audio_track_id: audioTrackId },
    )
    if (!response.ok) throwOnError(response, 'Failed to add track to playlist')
  },

  async removeTrackFromPlaylist(
    playlistId: number,
    audioTrackId: number,
  ): Promise<void> {
    const response = await client.delete<void>(
      MOH_ENDPOINTS.PLAYLIST_REMOVE_TRACK(playlistId, audioTrackId),
    )
    if (!response.ok)
      throwOnError(response, 'Failed to remove track from playlist')
  },
})
